import React from "react";
import { ConfigProvider, InputNumber, Slider } from "antd";
import type { ThemeConfig } from "antd";
import { currencyDisplay, parseCurrencyString } from "../../utils/helpers";

interface Props {
  value: [number, number];
  onChange: (value: [number, number]) => void;
  min: number;
  max: number;
  step: number;
  // ISO 4217 code used to format the amounts.
  currency?: string;
  // Lowest value the slider track draws; the range itself is still clamped to `min`.
  sliderMin?: number;
  sliderTheme?: ThemeConfig;
  sliderClassName?: string;
  rangeClassName: string;
  fieldClassName: string;
}

export default function BudgetRangeField({
  value,
  onChange,
  min,
  max,
  step,
  currency = "MYR",
  sliderMin = min,
  sliderTheme,
  sliderClassName,
  rangeClassName,
  fieldClassName,
}: Props) {
  const [low, high] = value;

  let slider = (
    <Slider
      range
      min={sliderMin}
      max={max}
      step={step}
      value={value}
      tooltip={{ open: false }}
      onChange={(next: number[]) => onChange([Math.max(next[0], min), Math.max(next[1], min)])}
    />
  );
  if (sliderTheme) slider = <ConfigProvider theme={sliderTheme}>{slider}</ConfigProvider>;
  if (sliderClassName) slider = <div className={sliderClassName}>{slider}</div>;

  return (
    <>
      {slider}
      <div className={rangeClassName}>
        <label className={fieldClassName}>
          <span>Minimum</span>
          <InputNumber
            min={min}
            max={high}
            step={step}
            value={low}
            formatter={(amount) => currencyDisplay(currency, amount ?? 0, 0)}
            parser={(text) => parseCurrencyString(text ?? "") || 0}
            onChange={(next) => onChange([Number(next ?? min), high])}
          />
        </label>
        <label className={fieldClassName}>
          <span>Maximum</span>
          <InputNumber
            min={low}
            max={max}
            step={step}
            value={high}
            formatter={(amount) => currencyDisplay(currency, amount ?? 0, 0)}
            parser={(text) => parseCurrencyString(text ?? "") || 0}
            onChange={(next) => onChange([low, Number(next ?? max)])}
          />
        </label>
      </div>
    </>
  );
}
